import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { AdaptationRecord, AdaptationUserResponse } from '../types/adaptive';

const HISTORY_SUBCOLLECTION = 'history';
const ADAPTIVE_COLLECTION = 'adaptive';

export async function getAdaptationHistory(
  userId: string,
  limit = 20,
): Promise<AdaptationRecord[]> {
  const q = query(
    collection(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION),
    orderBy('timestamp', 'desc'),
    firestoreLimit(limit),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => d.data() as AdaptationRecord)
    .filter((r) => r.profileChanges && r.profileChanges.length > 0);
}

export async function getAdaptationRecord(
  userId: string,
  recordId: string,
): Promise<AdaptationRecord | null> {
  const snap = await getDoc(
    doc(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION, recordId),
  );
  if (!snap.exists()) return null;
  return snap.data() as AdaptationRecord;
}

export async function respondToAdaptation(
  userId: string,
  recordId: string,
  response: {
    sentiment: 'positive' | 'negative' | 'neutral';
    comment: string | null;
    requestReversal: boolean;
  },
): Promise<void> {
  const ref = doc(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION, recordId);
  const update: Partial<AdaptationUserResponse> = {
    acknowledged: true,
    acknowledgedAt: new Date().toISOString(),
    sentiment: response.sentiment,
    userComment: response.comment,
    requestedReversal: response.requestReversal,
  };
  await updateDoc(ref, {
    'userResponse.acknowledged': update.acknowledged,
    'userResponse.acknowledgedAt': update.acknowledgedAt,
    'userResponse.sentiment': update.sentiment,
    'userResponse.userComment': update.userComment,
    'userResponse.requestedReversal': update.requestedReversal,
  });
}

export async function acknowledgeAllPending(userId: string): Promise<number> {
  const q = query(
    collection(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION),
    where('userResponse.acknowledged', '==', false),
  );
  const snap = await getDocs(q);
  const now = new Date().toISOString();
  const batch = await import('firebase/firestore').then((m) => m.writeBatch(db));
  for (const d of snap.docs) {
    batch.update(d.ref, {
      'userResponse.acknowledged': true,
      'userResponse.acknowledgedAt': now,
    });
  }
  await batch.commit();
  return snap.size;
}

export async function getUnacknowledgedCount(userId: string): Promise<number> {
  const q = query(
    collection(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION),
    where('userResponse.acknowledged', '==', false),
  );
  const snap = await getDocs(q);
  return snap.size;
}

export async function saveAdaptationRecord(record: AdaptationRecord): Promise<void> {
  await setDoc(
    doc(db, ADAPTIVE_COLLECTION, record.userId, HISTORY_SUBCOLLECTION, record.recordId),
    { ...record, _firestoreCreatedAt: serverTimestamp() },
  );
}

export async function applyReversal(
  userId: string,
  recordId: string,
): Promise<void> {
  const ref = doc(db, ADAPTIVE_COLLECTION, userId, HISTORY_SUBCOLLECTION, recordId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Record not found');
  const record = snap.data() as AdaptationRecord;

  const profileRef = doc(db, 'cogniprints', userId);
  const profileSnap = await getDoc(profileRef);
  if (!profileSnap.exists()) throw new Error('Profile not found');

  const profileData = profileSnap.data() as Record<string, unknown>;
  const updates: Record<string, unknown> = {};

  for (const change of record.profileChanges) {
    const dim = change.dimension;
    const existing = profileData[dim] as Record<string, unknown> | undefined;
    if (existing && typeof existing === 'object' && 'score' in existing) {
      if (typeof existing.score === 'number') {
        updates[`${dim}.score`] = change.previousScore;
        updates[`${dim}.confidence`] = change.confidenceBefore;
      } else if (typeof existing.score === 'object') {
        // Composite dimensions: revert isn't straightforward, skip for now
        continue;
      }
    }
  }

  if (Object.keys(updates).length > 0) {
    updates.lastUpdated = new Date().toISOString();
    await updateDoc(profileRef, updates);
  }

  await updateDoc(ref, {
    'userResponse.reversalApplied': true,
    'userResponse.reversalAppliedAt': new Date().toISOString(),
  });
}
